/**
 * AiMetaSection - Displays AI generation metadata for a screen
 *
 * Shows:
 * - Data mapping audit trail via DataMappingViewerV2 (rich viewer with categories, purposes)
 * - Generation info (model, timestamp, iteration count)
 * - Screen's role in the strategy block
 * - AI content sync status
 *
 * Used in AI editor mode only (mode='ai').
 */

import React, { useMemo } from 'react';
// Subpath import on purpose (same reason as DataMappingViewerV2): the
// ui-components BARREL drags data-pickers + firebase-core in at module scope.
import { SAccordion } from '@modules/ui-components/dialogs/SAccordion';
import { Box, Typography, Chip, Stack, Divider } from '@mui/material';
import { DataMappingViewerV2 } from '@modules/strategy-blocks/components/DataMappingViewerV2/DataMappingViewerV2';
import { useInsightEvidence } from '@modules/strategy-blocks/components/DataMappingViewerV2/useInsightEvidence';
import { MotivationSpecEditor } from './MotivationSpecEditor';
import type { BlockDataUsage } from '@segmently/schema-models/simplified';
import type { FlowStepNode } from '@modules/reactFlow/adapters/V2ScreenAdapter';

interface AiMetaSectionProps {
  step: FlowStepNode;
  locale: string;
  onChange: (path: string, value: unknown) => void;
  disabled?: boolean;
  defaultExpanded?: boolean;
  /**
   * W13 DM2 — needed to resolve the pinned analysis behind this screen's
   * `@insight[…]` citations. Absent (e.g. a preview with no project context):
   * the evidence rows still render, on the raw citation paths.
   */
  projectId?: string;
}

export const AiMetaSection: React.FC<AiMetaSectionProps> = ({
  step,
  onChange,
  disabled,
  defaultExpanded = true,
  projectId,
}) => {
  const aiMeta = (step as any).aiMeta;
  const aiContent = (step as any).aiContent;

  const generationModel = aiMeta?.generationModel ?? 'Unknown';
  const iterationCount = aiMeta?.iterationCount ?? 0;
  const blockRole = aiMeta?.blockRole;
  const generationTimestamp = aiMeta?.generationTimestamp;

  const hasAiData = aiContent || aiMeta;

  // Synthesize BlockDataUsage from aiMeta.dataMapping for the rich viewer
  const screenDataUsage = useMemo<BlockDataUsage | undefined>(() => {
    const dataMapping = aiMeta?.dataMapping;
    if (!dataMapping || dataMapping.length === 0) return undefined;
    return {
      items: dataMapping,
      purposes: [],
      entitySummary: '',
    } as unknown as BlockDataUsage;
  }, [aiMeta?.dataMapping]);

  // W13 DM2 — «Why this copy»: resolve the theses and the actor quotes this
  // screen cites. One read per cited analysis, through the ONE client reader.
  const sourcePaths = useMemo<unknown[]>(
    () => (aiMeta?.dataMapping ?? []).map((item: { sourcePath?: unknown }) => item?.sourcePath),
    [aiMeta?.dataMapping],
  );
  const insightEvidence = useInsightEvidence({ projectId, sourcePaths });

  return (
    <>
      <SAccordion
        title="AI Generation Info"
        defaultExpanded={defaultExpanded}
        collapsible
      >
        <Box data-testid="ai-meta-section" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {!hasAiData && (
            <Typography variant="body2" color="text.secondary">
              No AI generation data available for this screen.
            </Typography>
          )}

          {hasAiData && (
            <>
              {/* Generation Info */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>Generation Details</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip label={`Model: ${generationModel}`} size="small" variant="outlined" />
                  <Chip label={`Iterations: ${iterationCount}`} size="small" variant="outlined" />
                  {generationTimestamp && (
                    <Chip
                      label={`Generated: ${new Date(generationTimestamp).toLocaleDateString()}`}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Stack>
              </Box>

              {/* Block Role */}
              {blockRole && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Screen Role</Typography>
                  <Typography variant="body2" color="text.secondary">{blockRole}</Typography>
                </Box>
              )}

              {/* AI Content Preview */}
              {aiContent && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Simplified Content</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Content changes in editor sync to this AI version automatically.
                    Style changes are not synced (styles come from templates).
                  </Typography>
                </Box>
              )}
            </>
          )}

          {/* Motivation Spec — editable; available on any screen (not gated by hasAiData) */}
          <Divider sx={{ my: 0.5 }} />
          <MotivationSpecEditor
            spec={aiMeta?.motivationSpec}
            onChange={onChange}
            disabled={disabled}
          />
        </Box>
      </SAccordion>

      {/* Data Mapping — rendered as separate SAccordion (cannot be nested inside another SAccordion) */}
      {screenDataUsage && (
        <DataMappingViewerV2
          dataUsage={screenDataUsage}
          defaultExpanded={false}
          title="Why this copy"
          insightEvidence={insightEvidence}
        />
      )}
    </>
  );
};
